import { useEffect, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Checkbox,
} from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Education, Plus, Edit, Trash } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";

interface LedgerAccount {
  id?: number;
  code: string;
  name: string;
  reconcile: boolean;
  tagIds?: string[];
  accountType?: string;
  internalGroup?: string;
  deprecated: boolean;
  parentId?: number;
  companyId?: number;
  odooId?: number;
  company?: any;
}

const ACCOUNT_TYPES = [
  { value: "asset_fixed", label: "Vaste activa" },
  { value: "asset_current", label: "Vlottende activa" },
  { value: "asset_receivable", label: "Vorderingen" },
  { value: "asset_prepayments", label: "Overlopende rekeningen" },
  { value: "asset_cash", label: "Liquide middelen" },
  { value: "liability_payable", label: "Schulden" },
  { value: "liability_current", label: "Kortlopende schulden" },
  { value: "liability_non_current", label: "Langlopende schulden" },
  { value: "equity", label: "Eigen vermogen" },
  { value: "equity_unaffected", label: "Reserves" },
  { value: "income", label: "Opbrengsten" },
  { value: "income_other", label: "Overige opbrengsten" },
  { value: "expense", label: "Kosten" },
  { value: "expense_depreciation", label: "Afschrijvingen" },
];

const INTERNAL_GROUPS = [
  { value: "asset", label: "Activa" },
  { value: "liability", label: "Passiva" },
  { value: "equity", label: "Eigen vermogen" },
  { value: "income", label: "Opbrengsten" },
  { value: "expense", label: "Kosten" },
];

export const LedgerAccountsTab = () => {
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<LedgerAccount | null>(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    reconcile: false,
    deprecated: false,
    accountType: "",
    internalGroup: "",
    tagIds: "",
  });

  const { session } = useSession();

  useEffect(() => {
    fetchLedgerAccounts();
  }, []);

  const fetchLedgerAccounts = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/ledger-accounts/templates`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const accounts = await response.json();
      setLedgerAccounts(accounts);
    } catch (error) {
      console.error("Failed to load ledger accounts:", error);
      toast.error("Fout bij het laden van grootboekrekeningen.");
    }
  };

  const handleCreate = () => {
    setEditingAccount(null);
    setFormData({
      code: "",
      name: "",
      reconcile: false,
      deprecated: false,
      accountType: "",
      internalGroup: "",
      tagIds: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (account: LedgerAccount) => {
    setEditingAccount(account);
    setFormData({ 
      code: account.code,
      name: account.name,
      reconcile: account.reconcile,
      deprecated: account.deprecated,
      accountType: account.accountType || "",
      internalGroup: account.internalGroup || "",
      tagIds: Array.isArray(account.tagIds) ? account.tagIds.join(", ") : (account.tagIds || "")
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Weet je zeker dat je deze grootboekrekening wilt verwijderen?")) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/ledger-accounts/${id}`,
        { method: "DELETE" },
        session,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      toast.success("Grootboekrekening succesvol verwijderd.");
      fetchLedgerAccounts();
    } catch (error) {
      console.error("Failed to delete ledger account:", error);
      toast.error("Fout bij het verwijderen van grootboekrekening.");
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingAccount
        ? `${API_URL}/companies/ledger-accounts/${editingAccount.id}`
        : `${API_URL}/companies/ledger-accounts`;
      
      const method = editingAccount ? "PUT" : "POST";

      // Convert tagIds string to array
      const submitData = {
        ...formData,
        tagIds: formData.tagIds ? formData.tagIds.split(",").map(tag => tag.trim()).filter(tag => tag) : []
      };

      const response = await fetchWithAuth(
        url,
        {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submitData),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      toast.success(
        editingAccount
          ? "Grootboekrekening succesvol bijgewerkt."
          : "Grootboekrekening succesvol aangemaakt."
      );
      setIsModalOpen(false);
      fetchLedgerAccounts();
    } catch (error) {
      console.error("Failed to save ledger account:", error);
      toast.error("Fout bij het opslaan van grootboekrekening.");
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <header className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-1.5">
            <Education className="h-[18px] w-[18px]" />
            <p className="text-lg">Grootboekrekening templates</p>
          </div>
          <ButtonNew
            variant="primaryMoss"
            onClick={handleCreate}
            icon={<Plus />}
            label="Grootboekrekening toevoegen"
          />
        </header>

        <Table
          classNames={{
            wrapper: "shadow-none rounded-none p-0",
            thead: "h-fit border-b-2 border-neutral-primary/40",
            th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
            tbody: "border-b divide-y divide-neutral-primary/15",
            tr: "divide-x divide-neutral-primary/15",
            td: "py-2.5",
          }}
        >
          <TableHeader>
            <TableColumn>Code</TableColumn>
            <TableColumn>Naam</TableColumn>
            <TableColumn>Type</TableColumn>
            <TableColumn>Groep</TableColumn>
            <TableColumn>Labels</TableColumn>
            <TableColumn>Reconciliëren</TableColumn>
            <TableColumn>Actief</TableColumn>
            <TableColumn>Acties</TableColumn>
          </TableHeader>
          <TableBody>
            {ledgerAccounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>{account.code}</TableCell>
                <TableCell>{account.name}</TableCell>
                <TableCell>
                  {ACCOUNT_TYPES.find(t => t.value === account.accountType)?.label || account.accountType}
                </TableCell>
                <TableCell>
                  {INTERNAL_GROUPS.find(g => g.value === account.internalGroup)?.label || account.internalGroup}
                </TableCell>
                <TableCell>
                  {Array.isArray(account.tagIds) ? account.tagIds.join(", ") : (account.tagIds || "")}
                </TableCell>
                <TableCell>{account.reconcile ? "Ja" : "Nee"}</TableCell>
                <TableCell>{!account.deprecated ? "Ja" : "Nee"}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Edit
                      className="h-[18px] w-[18px] cursor-pointer text-brick-primary"
                      onClick={() => handleEdit(account)}
                    />
                    <Trash
                      className="h-[18px] w-[18px] cursor-pointer text-brick-primary"
                      onClick={() => handleDelete(account.id!)}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="2xl">
        <ModalContent>
          <ModalHeader>
            {editingAccount ? "Grootboekrekening bewerken" : "Nieuwe grootboekrekening"}
          </ModalHeader>
          <ModalBody>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                classNames={{
                  label: "text-neutral-primary font-medium",
                  input: "text-neutral-primary",
                }}
              />
              <Input
                label="Naam"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                classNames={{
                  label: "text-neutral-primary font-medium",
                  input: "text-neutral-primary",
                }}
              />
              <Select
                label="Account type"
                selectedKeys={formData.accountType ? [formData.accountType] : []}
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0] as string;
                  setFormData({ ...formData, accountType: selectedKey });
                }}
                classNames={{
                  label: "text-neutral-primary font-medium",
                  value: "text-neutral-primary",
                }}
              >
                {ACCOUNT_TYPES.map((type) => (
                  <SelectItem key={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </Select>
              <Select
                label="Interne groep"
                selectedKeys={formData.internalGroup ? [formData.internalGroup] : []}
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0] as string;
                  setFormData({ ...formData, internalGroup: selectedKey });
                }}
                classNames={{
                  label: "text-neutral-primary font-medium",
                  value: "text-neutral-primary",
                }}
              >
                {INTERNAL_GROUPS.map((group) => (
                  <SelectItem key={group.value}>
                    {group.label}
                  </SelectItem>
                ))}
              </Select>
              <Input
                label="Labels (gescheiden door komma's)"
                value={formData.tagIds}
                onChange={(e) => setFormData({ ...formData, tagIds: e.target.value })}
                placeholder="bv. Kapitaal, Reserves, Vastgoedbeleggingen"
                classNames={{
                  label: "text-neutral-primary font-medium",
                  input: "text-neutral-primary",
                }}
              />
              <div className="flex flex-col gap-2">
                <Checkbox
                  isSelected={formData.reconcile}
                  onValueChange={(checked) => setFormData({ ...formData, reconcile: checked })}
                  classNames={{
                    label: "text-neutral-primary font-medium",
                  }}
                >
                  Reconciliëren toestaan
                </Checkbox>
                <Checkbox
                  isSelected={!formData.deprecated}
                  onValueChange={(checked) => setFormData({ ...formData, deprecated: !checked })}
                  classNames={{
                    label: "text-neutral-primary font-medium",
                  }}
                >
                  Actief
                </Checkbox>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onPress={() => setIsModalOpen(false)}
            >
              Annuleren
            </Button>
            <Button
              className="bg-moss-primary text-white"
              onPress={handleSubmit}
            >
              {editingAccount ? "Bijwerken" : "Aanmaken"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}; 